import {
  EnvironmentId,
  type PullRequestDiffInput,
  type PullRequestDiffFileContentsInput,
  type PullRequestListInput,
  type PullRequestRef,
} from "@cmmd-forge/contracts";

function sortedRecord(record: Readonly<Record<string, string>> | undefined) {
  if (record === undefined) return undefined;
  return Object.fromEntries(
    Object.entries(record).toSorted(([left], [right]) => left.localeCompare(right)),
  );
}

function normalizedListInput(input: PullRequestListInput): PullRequestListInput {
  return {
    ...input,
    ...(input.projectIds === undefined
      ? {}
      : { projectIds: [...input.projectIds].toSorted((left, right) => left.localeCompare(right)) }),
    ...(input.cursors === undefined ? {} : { cursors: sortedRecord(input.cursors) }),
  };
}

export const pullRequestQueryKeys = {
  all: (environmentId: EnvironmentId) => ["pullRequests", environmentId] as const,
  list: (environmentId: EnvironmentId, input: PullRequestListInput) =>
    ["pullRequests", environmentId, "list", normalizedListInput(input)] as const,
  stats: (environmentId: EnvironmentId, refs: ReadonlyArray<PullRequestRef>) =>
    [
      "pullRequests",
      environmentId,
      "stats",
      [...refs].toSorted((left, right) =>
        `${left.projectId}:${left.repository}#${left.number}`.localeCompare(
          `${right.projectId}:${right.repository}#${right.number}`,
        ),
      ),
    ] as const,
  detail: (environmentId: EnvironmentId, reference: PullRequestRef) =>
    ["pullRequests", environmentId, "detail", reference] as const,
  activity: (environmentId: EnvironmentId, reference: PullRequestRef) =>
    ["pullRequests", environmentId, "activity", reference] as const,
  diff: (environmentId: EnvironmentId, input: Omit<PullRequestDiffInput, "cursor">) =>
    ["pullRequests", environmentId, "diff", input] as const,
  threadComments: (environmentId: EnvironmentId, input: object) =>
    ["pullRequests", environmentId, "thread-comments", input] as const,
  diffFileContents: (environmentId: EnvironmentId, input: PullRequestDiffFileContentsInput) =>
    ["pullRequests", environmentId, "diff-file-contents", input] as const,
  reviewerCandidates: (environmentId: EnvironmentId, reference: PullRequestRef) =>
    ["pullRequests", environmentId, "reviewer-candidates", reference] as const,
};

/**
 * The only scope that serves pull-request reads today; matches
 * `COMMAND_AUTHORITY_SCOPE` in `pullRequestReactQuery`.
 */
const COMMAND_AUTHORITY_SCOPE = EnvironmentId.make("command-authority");

/**
 * Cache-scan predicates for the two pull-request reads a badge may probe. Both pin the Command
 * authority scope: the same key shape under another scope answers a different reader, and a
 * badge must not mistake it for its own.
 */
export function isPullRequestDetailQueryKey(key: readonly unknown[]): boolean {
  return key[0] === "pullRequests" && key[1] === COMMAND_AUTHORITY_SCOPE && key[2] === "detail";
}

export function isPullRequestListQueryKey(key: readonly unknown[]): boolean {
  return key[0] === "pullRequests" && key[1] === COMMAND_AUTHORITY_SCOPE && key[2] === "list";
}
